import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar,
} from "recharts";
import { api, scopeParams } from "../lib/api";
import { useSession } from "../context/SessionContext";
import KpiCard from "../components/KpiCard";
import ModuleHero from "../components/ModuleHero";
import { StatusPill } from "../components/HealthPill";
import SizedChart from "../components/SizedChart";
import ExportButton from "../components/ExportButton";
import { downloadCSV, stamp } from "../lib/csv";
import { fmtInr, fmtInt, shortDate } from "../lib/format";

const Inventory = () => {
  const { session, focusBranchId } = useSession();
  const [category, setCategory] = useState("veg");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/inventory", { params: { ...scopeParams(session, focusBranchId), category } })
      .then((r) => mounted && setData(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [session, focusBranchId, category]);

  const categories = data?.categories || [];

  const exportCurrent = () => {
    if (!data) return;
    const catId = data.active_category?.id || category;
    const vt = data.view_type;
    let rows = [];
    let headers = [];
    if (vt === "expiry_risk" || vt === "bakery") {
      rows = data.expiry_risk || [];
      headers = [
        { key: "product", label: "Product" },
        { key: "brand", label: "Brand" },
        { key: "variant", label: "Variant" },
        { key: "branch_name", label: "Branch" },
        { key: "region", label: "Region" },
        { key: "days_to_expiry", label: "Days to Expiry" },
        { key: "qty", label: "Qty" },
        { key: "value", label: "Value (INR)" },
      ];
    } else if (vt === "brand_velocity") {
      // flatten brand_velocity into rows
      (data.brand_velocity || []).forEach((prod) => {
        prod.brands.forEach((b) => rows.push({
          product: prod.product, brand: b.brand,
          is_private_label: b.is_private_label ? "Y" : "",
          avg_days_on_shelf: b.avg_days_on_shelf,
          revenue_last30: b.revenue_last30,
          avg_margin_pct: b.avg_margin_pct,
        }));
      });
      headers = [
        { key: "product", label: "Product" },
        { key: "brand", label: "Brand" },
        { key: "is_private_label", label: "Private Label" },
        { key: "avg_days_on_shelf", label: "Avg. Days on Shelf" },
        { key: "revenue_last30", label: "Revenue Last 30D (INR)" },
        { key: "avg_margin_pct", label: "Avg. Margin %" },
      ];
    } else if (vt === "toiletries") {
      rows = data.stockout_risk || [];
      headers = [
        { key: "product", label: "Product" },
        { key: "brand", label: "Brand" },
        { key: "variant", label: "Variant" },
        { key: "branch_name", label: "Branch" },
        { key: "region", label: "Region" },
        { key: "stock_qty", label: "Stock" },
        { key: "daily_velocity", label: "Daily Velocity" },
        { key: "days_of_cover", label: "Days of Cover" },
        { key: "value", label: "Value (INR)" },
      ];
    } else if (vt === "dead_stock") {
      rows = data.dead_stock || [];
      headers = [
        { key: "product", label: "Product" },
        { key: "brand", label: "Brand" },
        { key: "variant", label: "Variant" },
        { key: "branch_name", label: "Branch" },
        { key: "region", label: "Region" },
        { key: "days_since_last_sale", label: "Days Since Last Sale" },
        { key: "bucket", label: "Aging Bucket" },
        { key: "stock_qty", label: "Qty" },
        { key: "value", label: "Value (INR)" },
      ];
    } else if (vt === "gift_seasonal") {
      rows = data.gift_seasonal || [];
      headers = [
        { key: "product", label: "Product" },
        { key: "brand", label: "Brand" },
        { key: "branch_name", label: "Branch" },
        { key: "region", label: "Region" },
        { key: "occasion", label: "Occasion" },
        { get: (r) => r.occasion_window ? `${r.occasion_window[0]} to ${r.occasion_window[1]}` : "", label: "Window" },
        { get: (r) => (r.past_window ? "Y" : ""), label: "Past Window" },
        { key: "days_past_window", label: "Days Past" },
        { key: "stock_qty", label: "Qty" },
        { key: "value", label: "Value (INR)" },
      ];
    }
    downloadCSV(`angadya-inventory-${catId}-${stamp()}.csv`, headers, rows);
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Module hero */}
      {!loading && data && <InventoryHero data={data} />}

      {/* Category selector + export */}
      <div className="ang-panel px-4 py-3 flex items-center gap-2 flex-wrap" data-testid="category-tabs">
        <span className="text-[10px] font-bold uppercase tracking-widest text-ang-muted mr-2">Category</span>
        {categories.map((c) => {
          const active = category === c.id;
          return (
            <button
              key={c.id}
              data-testid={`category-${c.id}`}
              onClick={() => setCategory(c.id)}
              className={`rounded-full text-xs font-bold px-3 py-1.5 transition-all ${
                active ? "text-white" : "text-ang bg-[#F5F6FA] hover:bg-[#EEF0F5]"
              }`}
              style={active ? {
                background: "linear-gradient(135deg, #00B4D8, #7C6FF0)",
                boxShadow: "0 4px 12px rgba(0,180,216,0.28)",
              } : {}}
            >
              {c.label}
            </button>
          );
        })}
        <div className="ml-auto">
          <ExportButton
            label="Export CSV"
            testId="btn-export-csv-inventory"
            onClick={exportCurrent}
            disabled={loading}
          />
        </div>
      </div>

      {loading ? (
        <SkeletonView />
      ) : (
        <CategoryView data={data} />
      )}
    </div>
  );
};

const SkeletonView = () => (
  <div className="ang-panel p-10 text-center text-sm text-ang-muted" data-testid="inventory-loading">
    Loading inventory…
  </div>
);

// Dynamic hero — adapts to the current category's view_type.
const InventoryHero = ({ data }) => {
  const vt = data.view_type;
  const k = data.kpis || {};
  const catLabel = data.active_category?.label || "Category";

  let primary = { label: "Category stock value", value: k.total_value || 0, countFormatter: (n) => fmtInr(n, { compact: true }) };
  let secondaries = [];
  let accent = "teal";
  let title = `${catLabel} · at a glance`;
  let subtitle = "Live snapshot across every branch in scope.";

  if (vt === "expiry_risk") {
    primary = { label: "Value at risk", value: k.at_risk_value || 0, countFormatter: (n) => fmtInr(n, { compact: true }) };
    secondaries = [
      { label: "Items ≤ 2 days", value: k.at_risk_items || 0, accent: "red" },
      { label: "Stock value", value: fmtInr(k.total_value || 0, { compact: true }) },
    ];
    accent = "red";
    subtitle = `Perishables expiring within 2 days across ${catLabel.toLowerCase()}.`;
  } else if (vt === "bakery") {
    primary = { label: "Wasted (30D)", value: k.total_wasted_30d || 0 };
    secondaries = [
      { label: "Waste %", value: `${k.waste_pct_30d}%`, accent: k.waste_pct_30d > 15 ? "red" : "amber" },
      { label: "Produced (30D)", value: k.total_produced_30d || 0 },
      { label: "At risk today", value: k.at_risk_items || 0, accent: "red" },
    ];
    accent = "amber";
    subtitle = `Batch production efficiency and waste for ${catLabel.toLowerCase()}.`;
  } else if (vt === "brand_velocity") {
    primary = { label: "Category stock value", value: k.total_value || 0, countFormatter: (n) => fmtInr(n, { compact: true }) };
    secondaries = [
      { label: "Line items", value: k.sku_count || 0 },
      { label: "Own-label SKUs", value: k.private_label_skus || 0, accent: "green" },
    ];
    accent = "violet";
    subtitle = `Which brands turn fastest in ${catLabel.toLowerCase()} — own-label vs. external.`;
  } else if (vt === "toiletries") {
    primary = { label: "Loyal brands at stockout risk", value: k.stockout_count || 0 };
    secondaries = [
      { label: "Dead-stock items · 30+ days", value: k.dead_stock_items || 0, accent: "amber" },
      { label: "Dead-stock value", value: fmtInr(k.dead_stock_value || 0, { compact: true }), accent: "amber" },
    ];
    accent = k.stockout_count > 0 ? "red" : "teal";
    subtitle = `Days-of-cover below 4 for loyalty-driven brands in ${catLabel.toLowerCase()}.`;
  } else if (vt === "dead_stock") {
    primary = { label: "Aging value · 60+ days", value: k.aging_value || 0, countFormatter: (n) => fmtInr(n, { compact: true }) };
    secondaries = [
      { label: "SKUs in scope", value: k.sku_count || 0 },
      { label: "Aging 90+ days", value: k.aging_90plus || 0, accent: "red" },
    ];
    accent = "amber";
    subtitle = `Slow-moving lines eating shelf space in ${catLabel.toLowerCase()}.`;
  } else if (vt === "gift_seasonal") {
    primary = { label: "Value stuck post-occasion", value: k.unsold_past_window_value || 0, countFormatter: (n) => fmtInr(n, { compact: true }) };
    secondaries = [
      { label: "Unsold past occasion", value: k.unsold_past_window || 0, accent: "amber" },
      { label: "Total gift stock", value: fmtInr(k.total_value || 0, { compact: true }) },
    ];
    accent = "violet";
    subtitle = `Seasonal SKUs still on shelf after the occasion window closed.`;
  }

  return (
    <ModuleHero
      testId="inventory-hero"
      eyebrow="Inventory Pulse"
      title={title}
      subtitle={subtitle}
      primary={primary}
      secondaries={secondaries}
      accent={accent}
    />
  );
};

const CategoryView = ({ data }) => {
  const vt = data.view_type;
  if (vt === "expiry_risk") return <ExpiryRiskView data={data} />;
  if (vt === "bakery") return <BakeryView data={data} />;
  if (vt === "brand_velocity") return <BrandVelocityView data={data} />;
  if (vt === "toiletries") return <ToiletriesView data={data} />;
  if (vt === "dead_stock") return <DeadStockView data={data} />;
  if (vt === "gift_seasonal") return <GiftSeasonalView data={data} />;
  return <div className="ang-panel p-6">Unknown view type: {vt}</div>;
};

// --------------------------------------------------------------------------

const ExpiryRiskView = ({ data }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <KpiCard testId="inv-kpi-at-risk-items" label="Items at risk" value={fmtInt(data.kpis.at_risk_items)} tone="danger" hint="≤ 2 days to expiry" />
      <KpiCard testId="inv-kpi-at-risk-value" label="Value at risk" value={fmtInr(data.kpis.at_risk_value, { compact: true })} tone="warn" hint="Perishables expiring soon" />
      <KpiCard testId="inv-kpi-total-value" label="Category stock value" value={fmtInr(data.kpis.total_value, { compact: true })} hint="Across scope" />
    </div>
    <PanelHeader eyebrow="Expiry Risk" title={`Items ≤ 2 days to expiry · ${data.active_category.label}`} />
    <div className="ang-panel">
      <table className="w-full text-sm" data-testid="expiry-risk-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
            <th className="text-left px-4 py-3">Product</th>
            <th className="text-left px-4 py-3">Branch</th>
            <th className="text-center px-4 py-3 w-32">Days to Expiry</th>
            <th className="text-right px-4 py-3 w-24">Qty</th>
            <th className="text-right px-4 py-3 w-32">Value</th>
          </tr>
        </thead>
        <tbody>
          {(data.expiry_risk || []).map((r, i) => (
            <tr key={i} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
              <td className="px-4 py-2.5">
                <div className="font-medium">{r.product}</div>
                <div className="text-xs text-ang-muted">{r.brand} · {r.variant}</div>
              </td>
              <td className="px-4 py-2.5">
                <div>{r.branch_name}</div>
                <div className="text-[11px] uppercase tracking-widest text-ang-muted">{r.region}</div>
              </td>
              <td className="px-4 py-2.5 text-center">
                <StatusPill band={r.days_to_expiry <= 1 ? "red" : "amber"}>
                  {r.days_to_expiry <= 0 ? "Expiring today" : `${r.days_to_expiry} day${r.days_to_expiry === 1 ? "" : "s"}`}
                </StatusPill>
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(r.qty)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{fmtInr(r.value)}</td>
            </tr>
          ))}
          {(data.expiry_risk || []).length === 0 && (
            <tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-ang-muted">Nothing at risk in this window.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </>
);

// --------------------------------------------------------------------------

const BakeryView = ({ data }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
      <KpiCard testId="bakery-kpi-at-risk" label="At risk · ≤ 1 day" value={fmtInt(data.kpis.at_risk_items)} tone="danger" />
      <KpiCard testId="bakery-kpi-waste-pct" label="Waste % · 30D" value={`${data.kpis.waste_pct_30d}%`} tone={data.kpis.waste_pct_30d > 15 ? "danger" : "warn"} />
      <KpiCard testId="bakery-kpi-produced" label="Produced · 30D" value={fmtInt(data.kpis.total_produced_30d)} />
      <KpiCard testId="bakery-kpi-wasted" label="Wasted · 30D" value={fmtInt(data.kpis.total_wasted_30d)} tone="warn" />
    </div>
    <PanelHeader eyebrow="Production planning" title="Produced vs. sold vs. wasted · daily" />
    <section className="ang-panel">
      <SizedChart height={300}>
          <BarChart data={data.bakery_production || []} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
            <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={30} axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip labelFormatter={shortDate} />
            <Bar dataKey="produced" fill="#14142B" name="Produced" />
            <Bar dataKey="sold" fill="#C9A15C" name="Sold" />
            <Bar dataKey="wasted" fill="#B7402A" name="Wasted" />
          </BarChart>
        </SizedChart>
    </section>
    <PanelHeader eyebrow="Expiry" title="Bakery SKUs ≤ 1 day to expiry" />
    <ExpiryTable rows={data.expiry_risk || []} />
  </>
);

const ExpiryTable = ({ rows }) => (
  <div className="ang-panel">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
          <th className="text-left px-4 py-3">Product</th>
          <th className="text-left px-4 py-3">Branch</th>
          <th className="text-center px-4 py-3 w-32">Days</th>
          <th className="text-right px-4 py-3 w-24">Qty</th>
          <th className="text-right px-4 py-3 w-32">Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
            <td className="px-4 py-2.5"><div className="font-medium">{r.product}</div><div className="text-xs text-ang-muted">{r.brand} · {r.variant}</div></td>
            <td className="px-4 py-2.5">{r.branch_name}<div className="text-[11px] uppercase tracking-widest text-ang-muted">{r.region}</div></td>
            <td className="px-4 py-2.5 text-center"><StatusPill band={r.days_to_expiry <= 0 ? "red" : "amber"}>{r.days_to_expiry <= 0 ? "Today" : `${r.days_to_expiry}D`}</StatusPill></td>
            <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(r.qty)}</td>
            <td className="px-4 py-2.5 text-right tabular-nums">{fmtInr(r.value)}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-ang-muted">All clear.</td></tr>}
      </tbody>
    </table>
  </div>
);

// --------------------------------------------------------------------------

const BrandVelocityView = ({ data }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <KpiCard testId="brand-kpi-total-value" label="Category stock value" value={fmtInr(data.kpis.total_value, { compact: true })} />
      <KpiCard testId="brand-kpi-sku-count" label="Line items" value={fmtInt(data.kpis.sku_count)} hint="Across branches in scope" />
      <KpiCard testId="brand-kpi-private-count" label="Own-label SKUs" value={fmtInt(data.kpis.private_label_skus)} />
    </div>

    <PanelHeader eyebrow="Brand Velocity" title="Avg. days-on-shelf by brand · lower is better" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {(data.brand_velocity || []).map((prod) => (
        <div key={prod.product} className="ang-panel p-5" data-testid={`velocity-${prod.product}`}>
          <div className="flex items-baseline justify-between border-b border-ang pb-2 mb-3">
            <h4 className="font-serif text-lg">{prod.product}</h4>
            <span className="text-[10px] uppercase tracking-widest text-ang-muted">{prod.brands.length} brands</span>
          </div>
          <div className="space-y-2">
            {prod.brands.map((b) => (
              <div key={b.brand} className="flex items-center gap-3 text-sm">
                <div className="w-32 truncate flex items-center gap-1.5">
                  {b.is_private_label && <span className="w-1 h-3.5 inline-block" style={{ background: "#C9A15C" }} />}
                  <span className={b.is_private_label ? "font-semibold" : ""}>{b.brand}</span>
                </div>
                <div className="flex-1 h-2 bg-[#E7E4DC] relative">
                  <div
                    className="absolute inset-y-0 left-0"
                    style={{
                      background: b.is_private_label ? "#C9A15C" : "#14142B",
                      width: `${Math.min(100, (b.avg_days_on_shelf / 20) * 100)}%`,
                    }}
                  />
                </div>
                <div className="w-14 text-right tabular-nums text-xs">{b.avg_days_on_shelf}d</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <PanelHeader eyebrow="Own-brand Margin" title="Own label vs. external brands" />
    <div className="ang-panel">
      <table className="w-full text-sm" data-testid="private-margin-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
            <th className="text-left px-4 py-3">Product</th>
            <th className="text-right px-4 py-3 w-40">Own-label Margin %</th>
            <th className="text-right px-4 py-3 w-40">Branded avg. Margin %</th>
            <th className="text-right px-4 py-3 w-40">Own-label Share of Rev</th>
          </tr>
        </thead>
        <tbody>
          {(data.private_label_margin || []).map((m, i) => (
            <tr key={i} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
              <td className="px-4 py-2.5 font-medium">{m.product}</td>
              <td className="px-4 py-2.5 text-right tabular-nums" style={{ color: "#C9A15C" }}>{m.private_margin_pct}%</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{m.branded_avg_margin_pct}%</td>
              <td className="px-4 py-2.5 text-right">
                <div className="flex items-center justify-end gap-2">
                  <div className="w-24 h-1.5 bg-[#E7E4DC] relative">
                    <div className="absolute inset-y-0 left-0" style={{ background: "#C9A15C", width: `${m.private_share * 100}%` }} />
                  </div>
                  <span className="tabular-nums text-xs w-12 text-right">{(m.private_share * 100).toFixed(1)}%</span>
                </div>
              </td>
            </tr>
          ))}
          {(data.private_label_margin || []).length === 0 && (
            <tr><td colSpan="4" className="px-4 py-8 text-center text-sm text-ang-muted">No private-label products in this category.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </>
);

// --------------------------------------------------------------------------

const ToiletriesView = ({ data }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <KpiCard testId="toi-kpi-stockout" label="Loyal brands at stockout risk" value={fmtInt(data.kpis.stockout_count)} tone="danger" hint="Days-of-cover < 4" />
      <KpiCard testId="toi-kpi-deadstock-items" label="Dead-stock items · 30+ days" value={fmtInt(data.kpis.dead_stock_items)} tone="warn" />
      <KpiCard testId="toi-kpi-deadstock-value" label="Dead-stock value" value={fmtInr(data.kpis.dead_stock_value, { compact: true })} tone="warn" />
    </div>
    <PanelHeader eyebrow="Loyal-brand stockout risk" title="Non-private brands running out — customers rarely substitute" />
    <div className="ang-panel">
      <table className="w-full text-sm" data-testid="toi-stockout-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
            <th className="text-left px-4 py-3">Product · Brand</th>
            <th className="text-left px-4 py-3">Branch</th>
            <th className="text-right px-4 py-3 w-24">Stock</th>
            <th className="text-right px-4 py-3 w-32">Daily Sell</th>
            <th className="text-right px-4 py-3 w-32">Days of Cover</th>
          </tr>
        </thead>
        <tbody>
          {(data.stockout_risk || []).map((r, i) => (
            <tr key={i} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
              <td className="px-4 py-2.5"><div className="font-medium">{r.product}</div><div className="text-xs text-ang-muted">{r.brand} · {r.variant}</div></td>
              <td className="px-4 py-2.5">{r.branch_name}<div className="text-[11px] uppercase tracking-widest text-ang-muted">{r.region}</div></td>
              <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(r.stock_qty)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{r.daily_velocity}</td>
              <td className="px-4 py-2.5 text-right"><StatusPill band={r.days_of_cover < 2 ? "red" : "amber"}>{r.days_of_cover}D</StatusPill></td>
            </tr>
          ))}
          {(data.stockout_risk || []).length === 0 && (
            <tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-ang-muted">No loyal-brand stockout risks right now.</td></tr>
          )}
        </tbody>
      </table>
    </div>
    <PanelHeader eyebrow="Dead stock" title="Aging toiletries · 30+ days without a sale" />
    <DeadStockTable rows={data.dead_stock || []} testId="toi-deadstock-table" />
  </>
);

// --------------------------------------------------------------------------

const DeadStockView = ({ data }) => {
  const bs = data.bucket_summary || {};
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KpiCard testId="ds-kpi-sku-count" label="SKUs in scope" value={fmtInt(data.kpis.sku_count)} />
        <KpiCard testId="ds-kpi-90plus" label="Aging 90+ days" value={fmtInt(data.kpis.aging_90plus)} tone="danger" />
        <KpiCard testId="ds-kpi-aging-value" label="Aging value · 60+ days" value={fmtInr(data.kpis.aging_value, { compact: true })} tone="warn" />
        <KpiCard testId="ds-kpi-buckets"
          label="Distribution"
          value={`${bs["90+"] || 0} / ${bs["60-89"] || 0} / ${bs["30-59"] || 0}`}
          hint="90+ / 60-89 / 30-59 days"
        />
      </div>
      <PanelHeader eyebrow="Aging inventory" title={`${data.active_category.label} · days since last sale`} />
      <DeadStockTable rows={data.dead_stock || []} testId="ds-table" />
    </>
  );
};

const DeadStockTable = ({ rows, testId }) => (
  <div className="ang-panel">
    <table className="w-full text-sm" data-testid={testId}>
      <thead>
        <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
          <th className="text-left px-4 py-3">Product</th>
          <th className="text-left px-4 py-3">Branch</th>
          <th className="text-center px-4 py-3 w-32">Days since last sale</th>
          <th className="text-center px-4 py-3 w-24">Bucket</th>
          <th className="text-right px-4 py-3 w-24">Qty</th>
          <th className="text-right px-4 py-3 w-32">Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 60).map((r, i) => (
          <tr key={i} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
            <td className="px-4 py-2.5"><div className="font-medium">{r.product}</div><div className="text-xs text-ang-muted">{r.brand} · {r.variant}</div></td>
            <td className="px-4 py-2.5">{r.branch_name}<div className="text-[11px] uppercase tracking-widest text-ang-muted">{r.region}</div></td>
            <td className="px-4 py-2.5 text-center tabular-nums">{r.days_since_last_sale}</td>
            <td className="px-4 py-2.5 text-center">
              <StatusPill band={r.bucket === "90+" ? "red" : r.bucket === "60-89" ? "amber" : "green"}>{r.bucket}</StatusPill>
            </td>
            <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(r.stock_qty)}</td>
            <td className="px-4 py-2.5 text-right tabular-nums">{fmtInr(r.value)}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-sm text-ang-muted">No aging stock.</td></tr>}
      </tbody>
    </table>
  </div>
);

// --------------------------------------------------------------------------

const GiftSeasonalView = ({ data }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <KpiCard testId="gift-kpi-past" label="Unsold past occasion" value={fmtInt(data.kpis.unsold_past_window)} tone="warn" />
      <KpiCard testId="gift-kpi-past-value" label="Value stuck post-occasion" value={fmtInr(data.kpis.unsold_past_window_value, { compact: true })} tone="danger" />
      <KpiCard testId="gift-kpi-total" label="Total gift stock value" value={fmtInr(data.kpis.total_value, { compact: true })} />
    </div>
    <PanelHeader eyebrow="Seasonal Sell-Through" title="Gift SKUs tracked against their occasion window" />
    <div className="ang-panel">
      <table className="w-full text-sm" data-testid="gift-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
            <th className="text-left px-4 py-3">Product</th>
            <th className="text-left px-4 py-3">Branch</th>
            <th className="text-left px-4 py-3">Occasion Window</th>
            <th className="text-center px-4 py-3 w-32">Status</th>
            <th className="text-right px-4 py-3 w-24">Qty Left</th>
            <th className="text-right px-4 py-3 w-32">Value</th>
          </tr>
        </thead>
        <tbody>
          {(data.gift_seasonal || []).slice(0, 100).map((r, i) => (
            <tr key={i} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
              <td className="px-4 py-2.5"><div className="font-medium">{r.product}</div><div className="text-xs text-ang-muted">{r.brand}</div></td>
              <td className="px-4 py-2.5">{r.branch_name}<div className="text-[11px] uppercase tracking-widest text-ang-muted">{r.region}</div></td>
              <td className="px-4 py-2.5 text-xs">
                <div className="font-medium text-sm">{r.occasion}</div>
                {r.occasion_window && (
                  <div className="text-ang-muted">{shortDate(r.occasion_window[0])} – {shortDate(r.occasion_window[1])}</div>
                )}
              </td>
              <td className="px-4 py-2.5 text-center">
                {r.past_window ? (
                  <StatusPill band={r.days_past_window > 60 ? "red" : "amber"}>{r.days_past_window}D past</StatusPill>
                ) : (
                  <StatusPill band="green">In window</StatusPill>
                )}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(r.stock_qty)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{fmtInr(r.value)}</td>
            </tr>
          ))}
          {(data.gift_seasonal || []).length === 0 && (
            <tr><td colSpan="6" className="px-4 py-8 text-center text-sm text-ang-muted">No gift stock in this scope.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </>
);

// --------------------------------------------------------------------------

const PanelHeader = ({ eyebrow, title }) => (
  <div className="flex items-end justify-between border-b border-ang pb-3 pt-2">
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ang-muted">{eyebrow}</div>
      <h3 className="font-serif text-xl mt-1">{title}</h3>
    </div>
  </div>
);

export default Inventory;
